import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "@/features/auth/services/authService";
import { HTTP_STATUS } from "@/config/constants";

type ResetPasswordState = {
  loading: boolean;
  error: unknown;
  isEmailSend: boolean;
  isValidRequest: boolean;
  isPasswordUpdated: boolean;
  email: string;
};

type ResetPasswordRootState = {
  resetPassword: ResetPasswordState;
};

type ResetPasswordPayload = Record<string, unknown>;

// Thunks
export const forgotPasswordThunk = createAsyncThunk(
  "resetPassword/forgotPassword",
  async (payload: ResetPasswordPayload, { rejectWithValue }) => {
    try {
      const res = await authService.forgotPassword(payload);
      if (res.status === HTTP_STATUS.OK && res.data) return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const validatePwdResetRequestThunk = createAsyncThunk(
  "resetPassword/validateToken",
  async (payload: string, { rejectWithValue }) => {
    try {
      const res = await authService.validateResetPassToken(payload);
      if (res.status === HTTP_STATUS.OK && res.data) return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response ? err.response.data.message : err);
    }
  }
);

export const resetPasswordThunk = createAsyncThunk(
  "resetPassword/resetPassword",
  async (payload: ResetPasswordPayload, { rejectWithValue }) => {
    try {
      const res = await authService.resetPassword(payload);
      if (res.status === HTTP_STATUS.CREATED) return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Slice
const initialState: ResetPasswordState = {
  loading: false,
  error: null,
  isEmailSend: false,
  isValidRequest: false,
  isPasswordUpdated: false,
  email: "",
};

const resetPasswordSlice = createSlice({
  name: "resetPassword",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // forgotPassword
      .addCase(forgotPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isEmailSend = false;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.isEmailSend = true;
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // validateToken
      .addCase(validatePwdResetRequestThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isValidRequest = false;
        state.email = "";
      })
      .addCase(validatePwdResetRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isValidRequest = true;
        state.email = action.payload.email;
      })
      .addCase(validatePwdResetRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isValidRequest = false;
      })
      // resetPassword
      .addCase(resetPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isPasswordUpdated = false;
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.isPasswordUpdated = true;
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetState } = resetPasswordSlice.actions;
export default resetPasswordSlice.reducer;

// Selectors
export const isEmailSendSuccessfullySelector = (
  state: ResetPasswordRootState
) => state.resetPassword.isEmailSend;
export const isValidRequestSelector = (state: ResetPasswordRootState) =>
  state.resetPassword.isValidRequest;
export const validatedEmailSelector = (state: ResetPasswordRootState) =>
  state.resetPassword.email;
export const isPasswordUpdatedSelector = (state: ResetPasswordRootState) =>
  state.resetPassword.isPasswordUpdated;
export const isLoadingSelector = (state: ResetPasswordRootState) =>
  state.resetPassword.loading;
export const isErrorSelector = (state: ResetPasswordRootState) =>
  state.resetPassword.error;
